import {
  ArrowRightIcon,
  CalendarIcon,
  CrownIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UsersIcon,
} from 'lucide-react';

import React from 'react';

import type { GroupCategory } from '@splitfiesta/core';
import { EXAMPLE_GROUP_ID, groupPro } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { GroupResponseDto, isEffectivelyZero } from '@splitfiesta/shared';
import { Badge, Card } from '@splitfiesta/ui';

import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { ProPill } from '@/components/pro/ProPill';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { LinkButton } from '@/components/ui/LinkButton';
import { RelativeTime } from '@/components/ui/RelativeTime';

import { getGroupCategoryIcon } from '@/utils/groupCategoryIcons';

interface GroupsTableProps {
  groups: GroupResponseDto[];
  showSeeAll?: boolean;
  seeAllLink?: string;
  seeAllText?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  showMemberCount?: boolean;
  showLastActivity?: boolean;
  className?: string;
  customAction?: React.ReactNode;
  lastElementRef?: (node: HTMLDivElement | null) => void;
  footer?: React.ReactNode;
  selectedGroupId?: string;
  /** The `pro` feature flag, read by the page that owns the list. */
  showProPills?: boolean;
}

export function GroupsTable({
  groups,
  showSeeAll = false,
  seeAllLink = '/groups',
  seeAllText,
  onLoadMore,
  hasMore = false,
  loading = false,
  emptyMessage,
  emptyDescription,
  emptyAction,
  showMemberCount = false,
  showLastActivity = true,
  className = '',
  customAction,
  lastElementRef,
  footer,
  selectedGroupId,
  showProPills = false,
}: GroupsTableProps) {
  const { t } = useTranslation();

  // Load members namespace for "Owner" translation
  useNamespace('members');

  if (loading && groups.length === 0) {
    return (
      <Card className="overflow-hidden">
        <div className="-mx-6">
          {[1, 2, 3, 4, 5].map(i => (
            <div
              key={i}
              className={`py-3 sm:py-4 flex items-center justify-between gap-3 sm:gap-4 px-4 sm:px-6 ${
                i !== 5 ? 'border-b border-gray-100 dark:border-gray-700' : ''
              }`}
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse"></div>
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-sm w-2/3 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-sm w-1/3 animate-pulse"></div>
                </div>
              </div>
              <div className="text-right">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-sm w-24 mb-1 animate-pulse"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-sm w-16 ml-auto animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (groups.length === 0) {
    return (
      <Card className="p-6" data-testid="groups-empty-state">
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">
            {emptyMessage || t('dashboard:hotGroups.empty')}
          </p>
          {emptyDescription && (
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {emptyDescription}
            </p>
          )}
          {emptyAction && <div className="mt-4">{emptyAction}</div>}
        </div>
      </Card>
    );
  }

  const getGroupBalance = (group: GroupResponseDto) => {
    // Use balance from GroupResponseDto first
    if (group.balance && Object.keys(group.balance).length > 0) {
      const totalBalance = Object.values(group.balance).reduce(
        (sum, amount) => sum + amount,
        0
      );
      return {
        amount: totalBalance,
        isOwed: totalBalance > 0,
      };
    }

    // Fallback to userBalance if available
    if (group.userBalance !== undefined && group.userBalance !== null) {
      return {
        amount: group.userBalance,
        isOwed: group.userBalance > 0,
      };
    }

    return { amount: 0, isOwed: false };
  };

  return (
    <>
      <Card
        className={`overflow-hidden ${className}`}
        data-testid="groups-grid"
      >
        <div className="-mx-6">
          {groups.map((group, index) => {
            const { amount, isOwed } = getGroupBalance(group);
            const lastActivityDate = group.lastActivityAt || group.createdAt;
            // Settlements in this group awaiting the user's confirmation —
            // drives the red attention bubble, mirroring the in-group
            // Settlements-tab badge and the mobile groups list.
            const pendingToConfirm = group.pendingSettlementsToConfirm ?? 0;
            const isPro =
              showProPills &&
              group.id !== EXAMPLE_GROUP_ID &&
              groupPro(group).active;

            return (
              <RouteChangeLink
                key={group.id}
                href={`/groups/${group.id}`}
                className="block group"
                data-testid={`group-card-${group.id}`}
              >
                <div
                  ref={index === groups.length - 1 ? lastElementRef : null}
                  className={`py-3 sm:py-4 flex items-center justify-between gap-3 sm:gap-4 hover:bg-[#F6F5F3] dark:hover:bg-gray-700/50 transition-colors px-4 sm:px-6 ${
                    selectedGroupId === group.id
                      ? 'bg-[#F6F5F3] dark:bg-gray-700/50 ring-2 ring-inset ring-orange-500 dark:ring-orange-400'
                      : ''
                  } ${
                    index !== groups.length - 1
                      ? 'border-b border-gray-100 dark:border-gray-700'
                      : ''
                  }`}
                >
                  {/* Left side: Icon and Group Info */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="relative shrink-0">
                      {React.createElement(
                        getGroupCategoryIcon(group.category as GroupCategory),
                        {
                          className:
                            'w-7 h-7 sm:w-8 sm:h-8 text-orange-500 dark:text-orange-400',
                        }
                      )}
                      {/* Pending-settlements notification badge — a small red
                          count on the group icon's corner (the universal
                          app-badge pattern) when the group has settlements
                          awaiting the user's confirmation. Mirrors the mobile
                          groups list + the in-group Settlements-tab badge,
                          without crowding the name or balance. */}
                      {pendingToConfirm > 0 && (
                        <span
                          className="absolute -top-1.5 -right-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white dark:ring-gray-800"
                          data-testid={`group-card-${group.id}-pending-settlements`}
                        >
                          {pendingToConfirm > 99 ? '99+' : pendingToConfirm}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-orange-500 dark:group-hover:text-orange-400 text-sm sm:text-base line-clamp-1 transition-colors">
                        {group.name}
                      </h4>
                      {group.description && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2 sm:line-clamp-1">
                          {group.description}
                        </p>
                      )}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-3 mt-0.5 sm:mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {showMemberCount && group.memberCount && (
                          <span className="flex items-center gap-1">
                            <UsersIcon className="w-3 h-3 hidden sm:inline-block" />
                            <span className="text-[11px] sm:text-xs">
                              {group.memberCount}{' '}
                              {group.memberCount === 1
                                ? t('common:labels.member')
                                : t('common:labels.members')}
                            </span>
                          </span>
                        )}
                        {showMemberCount &&
                          group.memberCount &&
                          showLastActivity &&
                          lastActivityDate && (
                            <span className="text-[10px] sm:text-xs hidden sm:inline">
                              •
                            </span>
                          )}
                        {showLastActivity && lastActivityDate && (
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3 hidden sm:inline-block" />
                            <span className="text-[11px] sm:text-xs">
                              <RelativeTime date={lastActivityDate} />
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right side: Balance */}
                  <div
                    className={`text-right shrink-0${isPro ? ' max-w-[45%]' : ''}`}
                  >
                    <div className="flex flex-wrap items-center justify-end gap-1 mb-0.5">
                      {isPro && <ProPill />}
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-sm">
                        {group.currency}
                      </span>
                      {/* The client-side example group wears an "Example"
                          badge (same as its detail hero) so nobody mistakes
                          its people and expenses for real. */}
                      {group.id === EXAMPLE_GROUP_ID && (
                        <Badge
                          variant="warning"
                          size="sm"
                          className="shrink-0 text-orange-700 dark:text-orange-300"
                          data-testid="example-badge"
                        >
                          <SparklesIcon className="w-3 h-3 mr-1" />
                          {t('groups:exampleGroup.tag')}
                        </Badge>
                      )}
                      {group.userRole === 'owner' && (
                        <Badge
                          variant="warning"
                          size="sm"
                          className="shrink-0"
                          data-testid="crown-icon"
                        >
                          <CrownIcon className="w-3 h-3 mr-1" />
                          {t('members:roles.owner')}
                        </Badge>
                      )}
                      {group.userRole === 'admin' && (
                        <Badge
                          variant="info"
                          size="sm"
                          className="shrink-0"
                          data-testid="admin-badge"
                        >
                          <ShieldCheckIcon className="w-3 h-3 mr-1" />
                          {t('members:roles.admin')}
                        </Badge>
                      )}
                    </div>
                    <div
                      className={`text-base sm:text-lg font-semibold ${
                        isEffectivelyZero(amount, group.currency)
                          ? 'text-gray-600 dark:text-gray-400'
                          : isOwed
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {isEffectivelyZero(amount, group.currency) ? (
                        t('common:labels.settled')
                      ) : (
                        <>
                          {isOwed ? '+' : '-'}{' '}
                          <CurrencyDisplay
                            amount={Math.abs(amount)}
                            currency={group.currency}
                          />
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {isEffectivelyZero(amount, group.currency)
                        ? ''
                        : isOwed
                          ? t('common:labels.youAreOwed')
                          : t('common:labels.youOwe')}
                    </p>
                  </div>
                </div>
              </RouteChangeLink>
            );
          })}
        </div>

        {/* Action button inside card */}
        {(showSeeAll || customAction) && (
          <div className="px-6 py-4">
            <div className="text-center">
              {customAction || (
                <LinkButton
                  href={seeAllLink}
                  size="base"
                  data-testid="dashboard-view-all-groups"
                >
                  {seeAllText || t('common:actions.seeAll')}
                  <ArrowRightIcon className="w-4 h-4 ml-1" />
                </LinkButton>
              )}
            </div>
          </div>
        )}

        {/* Footer section for Load More */}
        {footer}
      </Card>

      {/* Load more button outside card */}
      {hasMore && onLoadMore && (
        <div className="mt-4 text-center">
          <LinkButton onClick={onLoadMore} size="sm">
            {t('dashboard:hotGroups.loadMore', {
              count: groups.length,
            })}
          </LinkButton>
        </div>
      )}
    </>
  );
}
